import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { proposalLinePayload } from '../utils/leadPayloads';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function LeadProposals() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [proposals, setProposals] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [leads, setLeads] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [form, setForm] = useState({ lead_id: '', lead_opportunity_id: '', title: '', valid_until: '' });
  const [lineItems, setLineItems] = useState([{ description: '', quantity: 1, unit_price: '', discount_percent: 0 }]);

  const fetchProposals = useCallback(() => {
    if (!companyId || companyId === 'holding') return;
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: 20 });
    if (statusFilter) params.append('status', statusFilter);

    fetch(`${API}/companies/${companyId}/leads/proposals?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        setProposals(Array.isArray(data.data) ? data.data : []);
        setPagination({ current_page: data.current_page, last_page: data.last_page, total: data.total });
      })
      .catch(() => setProposals([]))
      .finally(() => setLoading(false));
  }, [companyId, page, statusFilter]);

  useEffect(() => { fetchProposals(); }, [fetchProposals]);

  useEffect(() => {
    if (!companyId || companyId === 'holding') return;
    fetch(`${API}/companies/${companyId}/leads?per_page=200`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(d => setLeads(Array.isArray(d.data) ? d.data : [])).catch(() => {});
    fetch(`${API}/companies/${companyId}/leads/opportunities/all?per_page=200`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(d => setOpportunities(Array.isArray(d.data) ? d.data : [])).catch(() => {});
  }, [companyId]);

  const createProposal = () => {
    let data;
    try { data = { ...form, line_items: proposalLinePayload(lineItems) }; }
    catch (error) { window.alert(error.message); return; }
    fetch(`${API}/companies/${companyId}/leads/proposals`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => {
        setShowCreate(false);
        setForm({ lead_id: '', lead_opportunity_id: '', title: '', valid_until: '' });
        setLineItems([{ description: '', quantity: 1, unit_price: '', discount_percent: 0 }]);
        fetchProposals();
      })
      .catch(() => window.alert('Failed to create proposal'));
  };

  const submitForApproval = (id) => {
    fetch(`${API}/companies/${companyId}/leads/proposals/${id}/submit`, {
      method: 'POST', headers: getAuthHeaders(),
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) { fetchProposals(); setShowDetail(null); } });
  };

  const approveProposal = (id) => {
    fetch(`${API}/companies/${companyId}/leads/proposals/${id}/approve`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ comments: '' }),
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) { fetchProposals(); setShowDetail(null); } });
  };

  const rejectProposal = (id) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    fetch(`${API}/companies/${companyId}/leads/proposals/${id}/reject`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ comments: reason }),
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) { fetchProposals(); setShowDetail(null); } });
  };

  const sendProposal = (id) => {
    fetch(`${API}/companies/${companyId}/leads/proposals/${id}/send`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ sent_via: 'email' }),
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) { fetchProposals(); setShowDetail(null); } });
  };

  const viewDetail = (id) => {
    fetch(`${API}/companies/${companyId}/leads/proposals/${id}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null).then(d => { if (d) setShowDetail(d); });
  };

  if (!companyId || companyId === 'holding') return <div style={{ padding: 32 }}><h2>Select a company</h2></div>;

  const statusColors = { draft: '#6b7280', awaiting_approval: '#f59e0b', approved: '#22c55e', rejected: '#ef4444', sent: '#3b82f6', awaiting_response: '#8b5cf6', accepted: '#10b981', declined: '#ef4444', expired: '#9ca3af' };
  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };

  const addLineItem = () => setLineItems([...lineItems, { description: '', quantity: 1, unit_price: '', discount_percent: 0 }]);
  const updateLine = (i, field, val) => { const n = [...lineItems]; n[i][field] = val; setLineItems(n); };
  const removeLine = (i) => setLineItems(lineItems.filter((_, idx) => idx !== i));

  const totalValue = lineItems.reduce((s, li) => {
    const sub = (Number(li.quantity) || 0) * (Number(li.unit_price) || 0);
    return s + sub - (sub * (Number(li.discount_percent) || 0) / 100);
  }, 0);

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>Proposals & Quotations</h2>
        <button onClick={() => setShowCreate(true)} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}>+ New Proposal</button>
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: 200 }}>
          <option value="">All Statuses</option>
          {['draft', 'awaiting_approval', 'approved', 'rejected', 'sent', 'awaiting_response', 'accepted', 'declined', 'expired'].map(s => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.08)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              {['Number', 'Title', 'Lead', 'Opportunity', 'Total Value', 'Status', 'Valid Until', 'Actions'].map((h, i) => (
                <th key={i} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {initialLoading ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
            ) : proposals.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No proposals found</td></tr>
            ) : proposals.map(p => (
              <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '10px 14px', fontWeight: 500, color: '#6366f1' }}>{p.proposal_number}</td>
                <td style={{ padding: '10px 14px', fontWeight: 600 }}>{p.title}</td>
                <td style={{ padding: '10px 14px' }}>{p.lead?.lead_name || '—'}</td>
                <td style={{ padding: '10px 14px' }}>{p.opportunity?.name || '—'}</td>
                <td style={{ padding: '10px 14px', fontWeight: 700, color: '#10b981' }}>{p.total_value ? '$' + Number(p.total_value).toLocaleString() : '—'}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: '#fff', background: statusColors[p.status] || '#6b7280' }}>
                    {p.status?.replace(/_/g, ' ')}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#9ca3af' }}>{p.valid_until ? new Date(p.valid_until).toLocaleDateString() : '—'}</td>
                <td style={{ padding: '10px 14px' }}>
                  <button onClick={() => viewDetail(p.id)} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #6366f1', color: '#6366f1', background: '#fff', borderRadius: 4, cursor: 'pointer' }}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          {Array.from({ length: pagination.last_page }, (_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: page === i + 1 ? '#6366f1' : '#fff', color: page === i + 1 ? '#fff' : '#374151', cursor: 'pointer', fontWeight: page === i + 1 ? 600 : 400 }}>
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 640, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <h3>{showDetail.title}</h3>
                <div style={{ fontSize: 13, color: '#6b7280' }}>{showDetail.proposal_number}</div>
              </div>
              <span style={{ padding: '4px 14px', borderRadius: 12, fontSize: 13, fontWeight: 600, color: '#fff', background: statusColors[showDetail.status] || '#6b7280' }}>{showDetail.status?.replace(/_/g, ' ')}</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13, marginBottom: 20 }}>
              <div><span style={{ color: '#6b7280' }}>Lead:</span> <strong>{showDetail.lead?.lead_name || '—'}</strong></div>
              <div><span style={{ color: '#6b7280' }}>Opportunity:</span> <strong>{showDetail.opportunity?.name || '—'}</strong></div>
              <div><span style={{ color: '#6b7280' }}>Total Value:</span> <strong style={{ color: '#10b981' }}>${Number(showDetail.total_value || 0).toLocaleString()}</strong></div>
              <div><span style={{ color: '#6b7280' }}>Valid Until:</span> <strong>{showDetail.valid_until ? new Date(showDetail.valid_until).toLocaleDateString() : '—'}</strong></div>
            </div>

            {/* Versions */}
            {showDetail.versions && showDetail.versions.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontWeight: 600, marginBottom: 8 }}>Line Items (Latest Version)</h4>
                {(() => {
                  const latestVersion = showDetail.versions[showDetail.versions.length - 1];
                  return (latestVersion?.line_items || []).map((li, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                      <span>{li.description}</span>
                      <span>{li.quantity} × ${Number(li.unit_price || 0).toLocaleString()} {li.discount_percent > 0 && <span style={{ color: '#ef4444' }}>(-{li.discount_percent}%)</span>}</span>
                    </div>
                  ));
                })()}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
              {showDetail.status === 'draft' && <button onClick={() => submitForApproval(showDetail.id)} style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#f59e0b', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Submit for Approval</button>}
              {showDetail.status === 'awaiting_approval' && <>
                <button onClick={() => approveProposal(showDetail.id)} style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#22c55e', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Approve</button>
                <button onClick={() => rejectProposal(showDetail.id)} style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#ef4444', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Reject</button>
              </>}
              {showDetail.status === 'approved' && <button onClick={() => sendProposal(showDetail.id)} style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#3b82f6', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Send to Client</button>}
              <button onClick={() => setShowDetail(null)} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 640, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: 20 }}>New Proposal</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1/-1' }}><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Title *</label><input style={inputStyle} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Lead *</label>
                <select style={inputStyle} value={form.lead_id} onChange={e => setForm({ ...form, lead_id: e.target.value, lead_opportunity_id: '' })}>
                  <option value="">Select lead</option>{leads.map(l => <option key={l.id} value={l.id}>{l.lead_name}</option>)}
                </select></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Opportunity</label>
                <select style={inputStyle} value={form.lead_opportunity_id} onChange={e => setForm({ ...form, lead_opportunity_id: e.target.value })}>
                  <option value="">Select opportunity</option>{opportunities.filter(o => !form.lead_id || String(o.lead_id) === String(form.lead_id)).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Valid Until</label><input style={inputStyle} type="date" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} /></div>
            </div>

            <h4 style={{ fontWeight: 600, marginTop: 20, marginBottom: 12 }}>Line Items</h4>
            {lineItems.map((li, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'end' }}>
                <div><label style={{ fontSize: 11, color: '#6b7280' }}>Description</label><input style={inputStyle} value={li.description} onChange={e => updateLine(i, 'description', e.target.value)} /></div>
                <div><label style={{ fontSize: 11, color: '#6b7280' }}>Qty</label><input style={inputStyle} type="number" min="1" value={li.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} /></div>
                <div><label style={{ fontSize: 11, color: '#6b7280' }}>Price</label><input style={inputStyle} type="number" value={li.unit_price} onChange={e => updateLine(i, 'unit_price', e.target.value)} /></div>
                <div><label style={{ fontSize: 11, color: '#6b7280' }}>Disc %</label><input style={inputStyle} type="number" value={li.discount_percent} onChange={e => updateLine(i, 'discount_percent', e.target.value)} /></div>
                <button onClick={() => removeLine(i)} style={{ border: 'none', background: '#fef2f2', color: '#ef4444', borderRadius: 4, padding: '8px', cursor: 'pointer', fontSize: 16 }}>×</button>
              </div>
            ))}
            <button onClick={addLineItem} style={{ fontSize: 13, padding: '6px 14px', border: '1px dashed #6366f1', color: '#6366f1', background: '#fff', borderRadius: 6, cursor: 'pointer' }}>+ Add Line</button>
            <div style={{ marginTop: 12, fontSize: 15, fontWeight: 700, color: '#10b981' }}>Total: ${totalValue.toLocaleString()}</div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowCreate(false)} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={createProposal} disabled={!form.title || !form.lead_id}
                style={{ padding: '8px 20px', border: 'none', borderRadius: 6, background: '#6366f1', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: form.title && form.lead_id ? 1 : 0.5 }}>Create Proposal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
